import React, { useState, ChangeEvent } from 'react';
import { addNewMenu, saveMenuFromScan, selectShouldOpen, selectMessage, selectSeverity, setOpen } from './slice';
import { MenuSaveRequestDTO } from './scan-types';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { AddDishContainer, AddDishTitle, AddDishContentHeader, AddDishContentBody } from './Menu.styled';
import { selectCurrentRestaurant, selectRestaurants } from '../Restaurant/slice';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useParams, useNavigate } from 'react-router-dom';
import PageContainer, { BreadCrumbLink } from '../../components/PageContainer/PageContainer';
import { NewMenuRequest } from '../MenuLoadRequest/types';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import MenuScanWizard, { MenuScanState } from './MenuScanWizard';
import { TabsContainer, Tab } from './MenuScanWizard.styled';

const AddMenu: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'url' | 'scan'>('scan');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const { alias } = useParams();
  const restaurant = useAppSelector(selectCurrentRestaurant);
  const allRestaurants = useAppSelector(selectRestaurants);

  const breadcrumbLinks: BreadCrumbLink[] = [
    {
      name: restaurant?.name ?? '',
      url: `/restaurant/${alias}`
    }
  ];

  const openServer = useAppSelector(selectShouldOpen);
  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const setOpenServer = (open: boolean): void => {
    dispatch(setOpen(open));
  };

  const handleSaveMenu = (): void => {
    const menuRequest: NewMenuRequest = {
      name,
      url,
      restaurantId: restaurant?.restaurantId ?? ''
    };
    void dispatch(addNewMenu(menuRequest));
    setName('');
    setUrl('');
  };

  const handleScanSubmit = async (data: MenuScanState): Promise<void> => {
    if (!data.scanId || data.scanId.trim() === '') {
      console.error('Cannot save menu: scanId is missing', data);
      dispatch(setOpen(true));
      return;
    }

    if (!data.menuName || data.menuName.trim() === '') {
      console.error('Cannot save menu: menuName is required');
      dispatch(setOpen(true));
      return;
    }

    const restaurantIdToUse = restaurant?.restaurantId ?? data.restaurant;
    if (!restaurantIdToUse) {
      console.error('Cannot save menu: restaurantId is missing');
      dispatch(setOpen(true));
      return;
    }

    // Build save request - dishes is optional
    // If omitted, backend will use stored scan data from the scan session
    // For now, we don't send dishes and let the backend reuse the stored scan result
    const saveRequest: MenuSaveRequestDTO = {
      restaurantId: restaurantIdToUse,
      menuName: data.menuName.trim()
      // dishes is omitted - backend will use stored scan data from scanId
    };

    console.log('Saving menu from scan:', {
      scanId: data.scanId,
      restaurantId: restaurantIdToUse,
      menuName: data.menuName,
      note: 'Backend will use stored scan data for dishes'
    });

    try {
      const result = await dispatch(saveMenuFromScan({ scanId: data.scanId, request: saveRequest })).unwrap();

      // Navigate to the new menu detail page
      // Route format: /restaurant/:alias/menu/:menuID
      if (result.menuId && alias) {
        // Show success message
        dispatch(setOpen(true));
        
        // Navigate to the menu detail page
        navigate(`/restaurant/${alias}/menu/${result.menuId}`);
      } else {
        console.error('Menu saved but menuId not returned', result);
        dispatch(setOpen(true));
        // Fallback: navigate to restaurant page
        navigate(`/restaurant/${alias}`);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message 
        ?? error?.response?.statusText 
        ?? error?.message 
        ?? 'Failed to save menu from scan';
      
      console.error('Save menu error:', {
        message: errorMessage,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
        scanId: data.scanId
      });
      
      dispatch(setOpen(true));
    }
  };

  const handleCancelWizard = (): void => {
    if (alias) {
      navigate(`/restaurant/${alias}`);
    }
  };

  return (
    <PageContainer title="Add Menu" breadcrumbs={breadcrumbLinks} open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
      <AddDishContainer>
        <AddDishContentHeader>
          <AddDishTitle>Add Menu</AddDishTitle>
        </AddDishContentHeader>

        {/* Tabs */}
        <TabsContainer>
          <Tab
            isActive={activeTab === 'scan'}
            onClick={() => setActiveTab('scan')}
          >
            Upload & Scan
          </Tab>
          <Tab
            isActive={activeTab === 'url'}
            onClick={() => setActiveTab('url')}
          >
            Add by URL
          </Tab>
        </TabsContainer>

        {/* Tab Content */}
        <AddDishContentBody>
          {activeTab === 'url' ? (
            // URL-based menu form
            <>
              <TextField
                fullWidth
                label="Menu Name"
                variant="outlined"
                value={name}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setName(event.target.value);
                }}
                size="small"
              />
              <TextField
                fullWidth
                label="URL"
                variant="outlined"
                value={url}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setUrl(event.target.value);
                }}
                size="small"
              />
              <Button variant="contained" size="small" onClick={handleSaveMenu} sx={{ marginTop: '16px' }}>
                Save
              </Button>
            </>
          ) : (
            // Scan-based menu wizard
            <MenuScanWizard
              restaurants={allRestaurants || []}
              restaurantId={restaurant?.restaurantId}
              restaurantName={restaurant?.name}
              restaurantAlias={alias}
              onCancel={handleCancelWizard}
              onSubmit={handleScanSubmit}
            />
          )}
        </AddDishContentBody>
      </AddDishContainer>
    </PageContainer>
  );
};

export default withAuthenticationRequired(AddMenu);
